import type {
  CommentAnchorSelection,
  LinkOpener,
  MentionSearchHandler,
  MentionSearchResult,
  UploadHandler,
} from "./types";

/** The document body cap from `markdown-dialect.md` §15, in ProseMirror nodes. */
export const DEFAULT_MAX_LENGTH = 1_536_000;

/**
 * Everything a plugin, command or node view needs from the host application.
 *
 * It is one mutable object rather than a set of React props threaded through
 * ProseMirror: plugins are created once for the life of a view, while props
 * change on every render, so the component keeps the object identity stable and
 * writes new callbacks into it. Reading `ctx.readOnly` at the moment a key is
 * pressed is therefore always current.
 */
export interface EditorContext {
  /** No editing at all (`editor.md` §15). */
  readOnly: boolean;
  /** Whether the reader may still tick checkboxes in read mode (§14). */
  canToggleTasks: boolean;
  placeholder: string;
  fullWidth: boolean;
  maxLength: number;
  /** The document being rendered; comment marks from elsewhere are dropped. */
  documentId: string | null;
  /** The signed-in user, for draft comment marks and awareness. */
  userId: string | null;
  /** Per-user preference: line numbers in code blocks (§5). */
  lineNumbers: boolean;
  /** Per-user preference: a dot in the margin beside every anchored line. */
  commentsInGutter: boolean;
  upload: UploadHandler | null;
  searchMentions: MentionSearchHandler | null;
  openLink: LinkOpener;
  onCommentClick: (commentId: string) => void;
  /** `Mod-Alt-M`: the comments UI applies the mark, the editor only asks. */
  onCreateComment: (selection: CommentAnchorSelection) => void;
  /**
   * Set by a single-purpose surface — the comment composer — where `Enter`
   * submits and `Shift-Enter` breaks the line (`comments.md` §15). Returning
   * false lets the ordinary `Enter` handling run.
   */
  onSubmit: (() => boolean) | null;
  /** Clicking an image in read mode (§5, Image). */
  onOpenLightbox: (src: string, alt: string | null) => void;
  /** Surfaced to the user when a paste is rejected or an upload fails. */
  onNotice: (message: string) => void;
  /**
   * The mention menu's "create a new document called …" entry (§2). Returns
   * the mention to insert, or null when the host declines.
   */
  createDocument: ((title: string) => Promise<MentionSearchResult | null>) | null;
  /** A document URL the paste path should turn into a mention (§8.3). */
  resolveDocumentUrl:
    ((url: string) => Promise<MentionSearchResult | null> | MentionSearchResult | null) | null;
}

export function createEditorContext(overrides: Partial<EditorContext> = {}): EditorContext {
  return {
    readOnly: false,
    canToggleTasks: true,
    placeholder: "",
    fullWidth: false,
    maxLength: DEFAULT_MAX_LENGTH,
    documentId: null,
    userId: null,
    lineNumbers: false,
    commentsInGutter: false,
    upload: null,
    searchMentions: null,
    openLink: (href) => {
      window.open(href, "_blank", "noopener,noreferrer");
    },
    onCommentClick: () => undefined,
    onCreateComment: () => undefined,
    onSubmit: null,
    onOpenLightbox: () => undefined,
    onNotice: () => undefined,
    createDocument: null,
    resolveDocumentUrl: null,
    ...overrides,
  };
}

/** Copy new prop values onto the live context without breaking its identity. */
export function updateEditorContext(
  context: EditorContext,
  updates: Partial<EditorContext>,
): EditorContext {
  Object.assign(context, updates);
  return context;
}
